import { PerfWindow } from 'src/utils/perf/aggregator';
import { SegmentFpsData } from 'src/utils/perf/types';

import { PerfSegment } from './types';
import { roundNullable } from './utils';

const SEGMENT_INTERVAL_MS = 30_000;
/**
 * 한 match window가 보유할 수 있는 30초 segment 최대 개수.
 * 60개 = 30분 커버리지. 이를 초과하는 매우 긴 매치에서는
 * 가장 오래된 segment부터 drop된다 (window-level percentile은 영향 없음).
 */
const MAX_SEGMENTS = 60;

const buildSegmentPayload = (
  index: number,
  startedAtEpoch: number,
  fromTimestamp: number,
  toTimestamp: number,
  data: SegmentFpsData
): PerfSegment => ({
  index,
  from_ms: Math.max(0, Math.round(fromTimestamp - startedAtEpoch)),
  to_ms: Math.max(0, Math.round(toTimestamp - startedAtEpoch)),
  page_fps_p50: roundNullable(data.pageFps?.p50),
  page_fps_p95: roundNullable(data.pageFps?.p95),
  page_fps_p05: roundNullable(data.pageFps?.p05),
  local_pipe_fps_p50: roundNullable(data.localPipeFps?.p50),
  local_pipe_fps_p95: roundNullable(data.localPipeFps?.p95),
  local_pipe_fps_p05: roundNullable(data.localPipeFps?.p05),
  local_send_pipe_fps_p50: roundNullable(data.localSendPipeFps?.p50),
  local_send_pipe_fps_p95: roundNullable(data.localSendPipeFps?.p95),
  local_send_pipe_fps_p05: roundNullable(data.localSendPipeFps?.p05),
  local_source_fps_p50: roundNullable(data.localSourceFps?.p50),
  local_source_fps_p95: roundNullable(data.localSourceFps?.p95),
  local_source_fps_p05: roundNullable(data.localSourceFps?.p05),
  local_encode_fps_p50: roundNullable(data.localEncodeFps?.p50),
  local_encode_fps_p95: roundNullable(data.localEncodeFps?.p95),
  local_encode_fps_p05: roundNullable(data.localEncodeFps?.p05),
  peer_in_fps_p50: roundNullable(data.peerInFps?.p50),
  peer_in_fps_p95: roundNullable(data.peerInFps?.p95),
  peer_in_fps_p05: roundNullable(data.peerInFps?.p05),
  peer_pipe_fps_p50: roundNullable(data.peerPipeFps?.p50),
  peer_pipe_fps_p95: roundNullable(data.peerPipeFps?.p95),
  peer_pipe_fps_p05: roundNullable(data.peerPipeFps?.p05),
});

/**
 * match window 하나의 라이프사이클과 동일한 30초 세그먼트 버퍼.
 * window 전환마다 새 인스턴스를 생성하면 되므로 수동 리셋이 필요 없다.
 */
export class SegmentCollector {
  private readonly startedAtEpoch: number;

  private buffer: PerfSegment[] = [];

  private nextIndex = 0;

  constructor(startedAtEpoch: number) {
    this.startedAtEpoch = startedAtEpoch;
  }

  /**
   * 30초 경계가 지난 segment만 buffer에 추가한다.
   * window 종료 시점에 마지막 30초 미만의 partial segment는 의도적으로 버린다 —
   * 시간 정렬된 균일한 비교를 위해서이고, 누락된 구간은 window-level
   * percentile에 이미 포함되므로 손실은 없다.
   */
  public tick(window: PerfWindow): void {
    const elapsed = Date.now() - this.startedAtEpoch;
    const expectedCount = Math.floor(elapsed / SEGMENT_INTERVAL_MS);

    while (this.nextIndex < expectedCount) {
      const fromTimestamp = this.startedAtEpoch + this.nextIndex * SEGMENT_INTERVAL_MS;
      const toTimestamp = fromTimestamp + SEGMENT_INTERVAL_MS;

      this.buffer.push(
        buildSegmentPayload(
          this.nextIndex,
          this.startedAtEpoch,
          fromTimestamp,
          toTimestamp,
          window.getSegmentSnapshot(fromTimestamp, toTimestamp)
        )
      );
      this.nextIndex += 1;

      if (this.buffer.length > MAX_SEGMENTS) {
        this.buffer.splice(0, this.buffer.length - MAX_SEGMENTS);
      }
    }
  }

  public getSegments(): PerfSegment[] {
    return this.buffer.slice();
  }
}
